<template>
    <div class="card mt-12">
        <div class="card-header">
            <div class="btn-toolbar justify-content-between mt-3" role="toolbar">
                <div class="form-inline col-lg-6 col-md-12">
                    <button value="hasCreateRights" type="button" class="btn btn-primary" @click.stop="createBtnClick">
                    {{ $t('buttons.create.btnText') }}
                    </button>
                </div>

                <div class="input-group col-lg-6 col-md-12">
                    <input v-model="searchQuery" v-on:keyup.enter.stop="massSearch"  v-on:keyup.esc.stop="clearFilters" type="text" class="form-control" aria-label="Mass search input" aria-describedby="massSearch">
                    <button class="btn btn-secondary" @click.stop="massSearch">
                        <i class="fas fa-search"></i>
                        {{ $t('buttons.search.btnText') }}
                    </button>
                    <button class="btn btn-secondary"
                            @click.stop="clearFilters">
                        <i class="fas fa-times"></i>
                        {{ $t('buttons.clearFilters.btnText') }}
                    </button>
                </div>
            </div>
        </div>

        <div class="card-body">
            <kendo-datasource ref="datasource1"
                              :transport="transportSettings"
                              :schema-model-id="'id'"
                              :schema-model-fields="schemaModelFields"
                              :schema-total="'total'"
                              :schema-data="'data'"
                              :page-size='defaultGridRows'
                              :server-paging="true"
                              :server-sorting="true"
                              :server-filtering="true"
                              :error="dataSourceError"
                              :requestEnd="datasorceRequestEnd">
            </kendo-datasource>

            <kendo-grid ref="grid"
                        :data-source-ref="'datasource1'"
                        :pageable="gridOptions.pageable"
                        :navigatable="gridOptions.navigatable"
                        :editable="gridOptions.editable"
                        :filterable="gridOptions.filterable"
                        :reorderable="gridOptions.reorderable"
                        :resizable="gridOptions.resizable"
                        :groupable="gridOptions.groupable"
                        :allow-copy="gridOptions.allowCopy"
                        :column-menu="gridOptions.columnMenu"
                        :sortable-mode="gridOptions.sortable.mode"
                        :sortable-allow-unsort="gridOptions.sortable.allowUnsort"
                        :sortable-show-indexes="gridOptions.sortable.showIndexes"
                        :selectable="gridOptions.selectable"
                        :scrollable="gridOptions.scrollable"
                        :toolbar="toolbar"
                        :excel="gridOptions.excel"
                        :pdf="gridOptions.pdf"
                        v-on:detailinit="detailInit"
                        :data-bound="dataBound">

                <kendo-grid-column :field="'id'"
                                   :width="100"
                                   :hidden="true"
                                   :title="$t('person.gridHeaders.id')">
                </kendo-grid-column>
                <kendo-grid-column :title="$t('actions')"
                                   field="btnsColumn"
                                   :sortable="false"
                                   :filterable="false"
                                   :template="btnsTemplate"
                                   :width="150">
                </kendo-grid-column>
                <kendo-grid-column :field="'userName'"
                                   :expandable="true"
                                   :title="$t('person.gridHeaders.username')">
                </kendo-grid-column>
                <kendo-grid-column :field="'email'"
                                   :title="$t('person.gridHeaders.email')">
                </kendo-grid-column>
                <kendo-grid-column :field="'roles'"
                                   :title="$t('person.gridHeaders.roles')">
                </kendo-grid-column>
                <kendo-grid-column :field="'roleModels'"
                                   :title="' '"
                                   :template="rolesCellTemplate"
                                   :editor="rolesDropDownEditor"
                                   :filterable="false"
                                   :hidden="true">
                </kendo-grid-column>
                <kendo-grid-column :field="'deleted'"
                                   :title="$t('person.gridHeaders.deleted')"
                                   :width="130"
                                   :template="deletedCellTemplate">
                </kendo-grid-column>
                <kendo-grid-column :field="'updatedAtUtc'"
                                   :title="$t('person.gridHeaders.updatedAtUtc')"
                                   :width="150"
                                   :format="customDatetimeFormat">
                </kendo-grid-column>
                <kendo-grid-column :field="'updatedByUsername'"
                                   :title="$t('person.gridHeaders.updatedBy')"
                                   :width="150">
                </kendo-grid-column>
            </kendo-grid>
        </div>

        <PersonEditModal v-model="personModel" :show="showPersonEdit" show-en-names @onClose="onPersonEditClose" @onSave="onPersonEdit"></PersonEditModal>
        <PersonDetailsModal v-model="personModel" :show="showPersonDetails" :allowEdit="hasPersonUpdateRights" @onEdit="onPersonEditClicked" @onClose="onPersonDetailsClose"></PersonDetailsModal>
        <UserCreateModal :show="showUserCreate" @onClose="onUserCreateClose" @onSave="onUserCreate"></UserCreateModal>
        <UserEditModal v-model="userModel" :show="showUserEdit" @onClose="onUserEditClose" @onSave="onUserEdit"></UserEditModal>
    </div>

</template>

<script>
    import $ from 'jquery'; // това се изисква за да се вмъкват kendo ui компоненти. Виж метод parentIdDropDownEditor
    import Vue from 'vue';
    import JSZip from 'jszip'
    import BtnsTemplate from "@/components/grid/StandardBtnsTemplate";
    import PersonEditModal from '@/components/person/edit.modal';
    import PersonDetailsModal from '@/components/person/details.modal';
    import UserEditModal from '@/components/user/edit.modal'
    import UserCreateModal from '@/components/user/create.modal'
    import userService from '@/services/user.service';
    import personService from '@/services/person.service';
    import { mapGetters } from 'vuex';

    /*
    Renders the grid in "DejaVu Sans" font family, which is
    declared in kendo.common.css. It also declares the paths to the
    fonts below using <tt>kendo.pdf.defineFont</tt>, because the
    stylesheet is hosted on a different domain.
    */
    kendo.pdf.defineFont({
        "DejaVu Sans": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans.ttf",
        "DejaVu Sans|Bold": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Bold.ttf",
        "DejaVu Sans|Bold|Italic": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf",
        "DejaVu Sans|Italic": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/DejaVu/DejaVuSans-Oblique.ttf",
        "WebComponentsIcons": "https://kendo.cdn.telerik.com/2019.2.619/styles/fonts/glyphs/WebComponentsIcons.ttf"
    });

    export default {
        components: {
            PersonEditModal,
            PersonDetailsModal,
            UserEditModal,
            UserCreateModal
        },
        props: {
            expandFirstMasterDetails: {
                default: false,
                type: Boolean
            }
        },
        data() {
            return {
                componentName: "usersListGrid", // Трябва на компонентата за бутоните за дашборд настройките
                titleKey: 'administration.user.grid.title', // Трябва на компонентата за бутоните за дашборд настройките
                defaultGridRows: this.$store.getters.defaultGridRows,
                settingsTooltipText: this.$t('buttons.settings.tooltipText'),
                searchQuery: '',
                showPersonEdit: false,
                personModel: null,
                showPersonDetails: false,
                userModel: null,
                showUserEdit: false,
                showUserCreate: false,
                schemaModelFields: {
                    id: { name: 'id', type: 'string', editable: false, nullable: false, visible: true },
                    userName: {
                        name: 'userName',
                        type: 'string',
                        editable: true,
                        nullable: true,
                        visible: true,
                        validation: { required: true, type: 'text' }
                    },
                    email: {
                        name: 'email',
                        type: 'string',
                        editable: true,
                        nullable: true,
                        visible: true,
                        validation: { required: true, type: 'email' }
                    },
                    roles: { name: 'email', type: 'string', editable: false, nullable: true, visible: false },
                    roleModels: {
                        name: 'roleModels',
                        type: 'object',
                        editable: true,
                        nullable: true,
                        visible: true,
                        defaultValue: { id: 0, name: 'Избери...', normalizedName: '' }
                    },
                    deleted: { name: 'deleted', type: 'boolean', editable: false, nullable: true, visible: true },
                    updatedAtUtc: {
                        name: 'updatedAtUtc',
                        type: 'date',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                    updatedByUsername: {
                        name: 'updatedByUsername',
                        type: 'string',
                        editable: false,
                        nullable: true,
                        visible: true
                    },
                },
                personSchemaModelFields: {
                    id: { name: 'id', type: 'number', editable: false, nullable: false, visible: true, titleKey: 'person.gridHeaders.id' },
                    firstName: { name: 'firstName', type: 'string', editable: true, nullable: true, visible: true, titleKey: 'person', validation: { required: true, type: 'text' } },
                    middleName: { name: 'middleName', type: 'string', editable: true, nullable: true, visible: true, titleKey: 'person' },
                    lastName: { name: 'lastName', type: 'string', editable: true, nullable: true, visible: true, titleKey: 'person', validation: { required: true, type: 'text' } },
                    fullName: { name: 'fullName', type: 'string', editable: false, nullable: true, visible: true, titleKey: 'person' },
                    identificationType: { name: 'identificationType', type: 'string', editable: true, nullable: true, visible: true, titleKey: 'person' },
                    identificationNumber: { name: 'identificationNumber', type: 'string', editable: true, nullable: true, visible: true, titleKey: 'person' },
                    phone: { name: 'phone', type: 'string', editable: true, nullable: true, visible: true, titleKey: 'person' },
                    mobilePhone: { name: 'mobilePhone', type: 'string', editable: true, nullable: true, visible: true, titleKey: 'person' },
                    email: { name: 'email', type: 'string', editable: true, nullable: true, visible: true, titleKey: 'person', validation: { required: true, type: 'email' } },
                    institutionRegister: { name: 'institutionRegister', type: 'string', editable: true, nullable: true, visible: true, titleKey: 'person' },
                    institutionRegisterLocation: { name: 'institutionRegisterLocation', type: 'string', editable: true, nullable: true, visible: true, titleKey: 'person' },
                    position: { name: 'position', type: 'string', editable: true, nullable: true, visible: true, titleKey: 'person' },
                    positionEn: { name: 'positionEn', type: 'string', editable: false, nullable: true, visible: true, titleKey: 'person' },
                    description: { name: 'description', type: 'string', editable: true, nullable: true, visible: true, titleKey: 'person' },
                    gender: { name: 'gender', type: 'string', editable: true, nullable: true, visible: true, titleKey: 'person' },
                    genderEn: { name: 'genderEn', type: 'string', editable: false, nullable: true, visible: true, titleKey: 'person' },
                    parentId: { name: 'parentId', type: 'number', editable: false, nullable: false, visible: true, titleKey: 'person' },
                    hasParent: { name: 'hasParent', type: 'boolean', editable: false, nullable: false, visible: true, titleKey: 'registers.organizers.gridHeaders.active' },
                    parentFirstName: { name: 'parentFirstName', type: 'string', editable: false, nullable: true, visible: true, titleKey: 'person' },
                    parentMiddleName: { name: 'parentMiddleName', type: 'string', editable: false, nullable: true, visible: true, titleKey: 'person' },
                    parentLastName: { name: 'parentLastName', type: 'string', editable: false, nullable: true, visible: true, titleKey: 'person' },
                    parentFullName: { name: 'parentFullName', type: 'string', editable: false, nullable: true, visible: true, titleKey: 'person' },
                    username: { name: 'username', type: 'string', editable: false, nullable: true, visible: true, titleKey: 'person' },
                    hasChanges: { name: 'hasChanges', type: 'boolean', editable: false, nullable: false, visible: true, titleKey: 'registers.organizers.gridHeaders.active' },
                    educationDegree: { name: 'educationDegree', type: 'string', editable: true, nullable: true, visible: true, titleKey: 'person' },
                    educationDegreeEn: { name: 'educationDegreeEn', type: 'string', editable: true, nullable: true, visible: true, titleKey: 'person' },
                    titleDegree: { name: 'titleDegree', type: 'string', editable: true, nullable: true, visible: true, titleKey: 'person' },
                    titleDegreeEn: { name: 'titleDegreeEn', type: 'string', editable: true, nullable: true, visible: true, titleKey: 'person' },
                    updatedAtUtc: { name: 'updatedAtUtc', type: 'date', editable: false, nullable: true, visible: true, titleKey: 'person' },
                    updatedByUsername: { name: 'updatedByUsername', type: 'string', editable: false, nullable: true, visible: true, titleKey: 'person' },
                },
                transportSettings: {
                    read: {
                        url: this.$store.getters.baseUrl + '/api/user/listDetails',
                        dataType: 'json',
                        type: 'POST',
                        contentType: 'application/json',
                        beforeSend: this.beforeSend
                    },
                    parameterMap: this.parameterMap
                },
                gridOptions: {
                    ...this.$store.getters.gridOptions,
                    editable: {
                        mode: 'popup', /* inline, popup */
                        window: {
                            title: this.$tc('administration.user.title', 2),
                            animation: true
                        },
                        move: true
                    },
                    excel: {
                        fileName: 'Users.xlsx',
                        allPages: false,
                        filterable: true,
                        collapsible: true
                    },
                },
                working: false
            };
        },
        methods: {
            dataBound(ev) {
                if (this.expandFirstMasterDetails) {
                    ev.sender.expandRow(ev.sender.tbody.find('tr.k-master-row').first())
                }
            },
            parameterMap: function (options, operation) {
                if (operation !== 'read' && options.models) {
                    return { models: kendo.stringify(options.models) }
                } else {
                    // Викаме POST action за листване на номенклатурите и трябва да сериализираме
                    // options. Така се предава информация за take, skip , page и списък с обекти
                    // описващи сортирането
                    return kendo.stringify(options);
                }
            },
            beforeSend: function (req) {
                if (this.token) {
                    req.setRequestHeader('Authorization', 'Bearer ' + this.token);
                }
            },
            detailInit: function (e) {
                $('<div />').appendTo(e.detailCell).kendoGrid({
                    dataSource: {
                        transport: {
                            read: {
                                url: this.$store.getters.baseUrl + '/api/person/listDetails',
                                dataType: 'json',
                                type: 'POST',
                                contentType: 'application/json',
                                beforeSend: this.beforeSend
                            },
                            parameterMap: this.parameterMap
                        },
                        serverPaging: false,
                        serverSorting: false,
                        serverFiltering: false,
                        filter: { // това филтрира само елементите за дадена родителска категория
                            field: 'userId',
                            operator: 'eq',
                            value: e.data.id
                        },
                        schema: {
                            model: {
                                id: 'id',
                                fields: this.personSchemaModelFields
                            },
                            total: 'total',
                            data: 'data'
                        },
                        error: this.dataSourceError,
                        requestEnd: this.datasorceRequestEnd
                    },
                    editable: false,
                    pageable: false,
                    scrollable: this.gridOptions.scrollable,
                    sortable: this.gridOptions.sortable,
                    toolbar: [],
                    dataBound: function (e) {
                        let editLink = e.sender.element.find('.k-grid-customEdit');
                        if (!editLink.hasClass('custom-edit-btn')) {
                            editLink.addClass("btn btn-sm btn-success custom-edit-btn")
                            editLink.prepend('<span data-toggle="tooltip" title="' + this.$t('buttons.edit.btnText') + '"><i class="fas fa-edit"></i></span>');
                        }

                        let detailsLink = e.sender.element.find(".k-grid-customDetails");
                        if (!detailsLink.hasClass('custom-details-btn')) {
                            detailsLink.addClass("btn btn-sm btn-secondary custom-details-btn mt-2")
                            detailsLink.prepend('<span data-toggle="tooltip" title="' + this.$t('buttons.details.btnText') + '"><i class="fas fa-eye"></i></span>');
                        }

                        var items = e.sender.items();
                        items.each(function (idx, item) {
                            let dataItem = e.sender.dataItem(item);
                            if (!dataItem.hasParent) {
                                $(item).addClass('left-border-green');
                            }
                        });
                    }.bind(this),
                    columns: [
                        {
                            width: 100,
                            command:
                                this.hasUpdateRights
                                    ? [
                                        {
                                            name: 'customEdit',
                                            text: " ",
                                            click: this.personEdit
                                        },
                                        {
                                            name: 'customDetails',
                                            text: " ",
                                            click: this.personDetails
                                        }
                                    ]
                                    : []
                        },
                        {
                            field: 'id',
                            title: this.$t('person.gridHeaders.id'),
                            width: 100,
                        },
                        {
                            field: 'fullName',
                            title: this.$t('person.gridHeaders.fullname'),
                            width: 200,
                        },
                        {
                            field: 'username',
                            title: this.$t('person.gridHeaders.username'),
                            width: 150,
                        },
                        {
                            field: 'firstName',
                            title: this.$t('person.gridHeaders.firstName'),
                            width: 150,
                            hidden: true
                        },
                        {
                            field: 'middleName',
                            title: this.$t('person.gridHeaders.middleName'),
                            width: 150,
                            hidden: true
                        },
                        {
                            field: 'lastName',
                            title: this.$t('person.gridHeaders.lastName'),
                            width: 150,
                            hidden: true
                        },
                        {
                            field: 'identificationType',
                            title: this.$t('person.gridHeaders.identificationType'),
                            width: 100,
                        },
                        {
                            field: 'identificationNumber',
                            title: this.$t('person.identification'),
                            width: 150,
                        },
                        {
                            field: 'phone',
                            title: this.$t('person.phone'),
                            width: 150,
                        },
                        {
                            field: 'mobilePhone',
                            title: this.$t('person.mobilePhone'),
                            width: 150,
                        },
                        {
                            field: 'email',
                            title: this.$t('person.gridHeaders.email'),
                            width: 150,
                        },
                        {
                            field: 'institutionRegister',
                            title: this.$t('person.institution'),
                            width: 150,

                        },
                        {
                            field: 'institutionRegisterLocation',
                            title: this.$t('person.institutionLocation'),
                            width: 150,
                        },
                        {
                            field: 'position',
                            title: this.$t('person.position'),
                            width: 150,
                            hidden: this.isEnVersion,

                        },
                        {
                            field: 'positionEn',
                            title: this.$t('person.position'),
                            width: 150,
                            hidden: !this.isEnVersion,
                        },
                        {
                            field: 'description',
                            title: this.$t('person.description'),
                            width: 150,
                        },
                        {
                            field: 'gender',
                            title: this.$t('person.gender'),
                            width: 150,
                            hidden: this.isEnVersion,
                        },
                        {
                            field: 'genderEn',
                            title: this.$t('person.gender'),
                            width: 150,
                            hidden: !this.isEnVersion,
                        },
                        {
                            field: 'titleDegree',
                            title: this.$t('person.titleDegree'),
                            width: 150,
                            hidden: this.isEnVersion,
                        },
                        {
                            field: 'titleDegreeEn',
                            title: this.$t('person.titleDegree'),
                            width: 150,
                            hidden: !this.isEnVersion,
                        },
                        {
                            field: 'educationDegree',
                            title: this.$t('person.educationDegree'),
                            width: 150,
                            hidden: this.isEnVersion,
                        },
                        {
                            field: 'educationDegreeEn',
                            title: this.$t('person.educationDegree'),
                            width: 150,
                            hidden: !this.isEnVersion,
                        },
                        {
                            field: 'hasParent',
                            title: this.$t('person.gridHeaders.hasParent'),
                            width: 150,
                            template: this.hasParentCellTemplate
                        },
                        {
                            field: 'parentId',
                            title: this.$t('person.gridHeaders.parentId'),
                            width: 150,
                        },
                        {
                            field: 'parentFullName',
                            title: this.$t('person.gridHeaders.parent'),
                            width: 150,
                        },
                        {
                            field: 'updatedAtUtc',
                            title: this.$t('person.gridHeaders.updatedAtUtc'),
                            format: this.customDatetimeFormat,
                            width: 150,
                        },
                        {
                            field: 'updatedByUsername',
                            title: this.$t('person.gridHeaders.updatedByUsername'),
                            width: 150,
                        }
                    ]
                })
            },
            personEdit(e) {
                e.preventDefault();
                if (!this.hasPersonUpdateRights) {
                    return this.$router.push(`/unauthorized`);
                }

                var tr = $(e.currentTarget).closest("tr");
                var detailsGrid = tr.closest('.k-grid').data("kendoGrid");
                var dataItem = detailsGrid.dataItem(tr);
                if (dataItem) {
                    this.showPersonEditModal(Number(dataItem.id));
                }
            },
            personDetails(e) {
                e.preventDefault();
                if (!this.hasPersonReadRights) {
                    return this.$router.push(`/unauthorized`);
                }

                var tr = $(e.currentTarget).closest("tr");
                var detailsGrid = tr.closest('.k-grid').data("kendoGrid");
                var dataItem = detailsGrid.dataItem(tr);
                if (dataItem) {
                    this.showPersonDetailsModal(Number(dataItem.id));
                }
            },
            showPersonDetailsModal(id) {
                personService
                    .getById(id)
                    .then(response => {
                        let model = response.data;
                        if (model) {
                            this.personModel = model;
                            this.showPersonDetails = true;
                        } else {
                            this.editModel = null;
                            this.showPersonDetails = true;
                        }
                    })
                    .catch(error => {
                        this.personModel = null;
                        this.showPersonDetails = false;

                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        })
                    });
            },
            showPersonEditModal(id) {
                personService
                    .getById(id)
                    .then(response => {
                        let model = response.data;
                        if (model) {
                            this.personModel = model;
                            this.showPersonEdit = true;
                        } else {
                            this.editModel = null;
                            this.showPersonEdit = false;
                        }
                    })
                    .catch(error => {
                        this.personModel = null;
                        this.showPersonEdit = false;

                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        })
                    });
            },
            onPersonEditClose() {
                this.showPersonEdit = false;
                this.personModel = null;
            },
            onPersonDetailsClose() {
                this.showPersonDetails = false;
                this.personModel = null;
            },
            onPersonEdit(item) {
                this.refresh();
            },
            onPersonEditClicked(item) {
                this.showPersonEditModal(item.id);
            },
            onUserEditClose() {
                this.showUserEdit = false;
                this.userModel = null;
            },
            onUserEdit(item) {
                this.showUserEdit = false;
                this.userModel = null;
                this.refresh();
            },
            onUserCreateClose() {
                this.showUserCreate = false;
                this.userModel = null;
            },
            onUserCreate(item) {
                this.showUserCreate = false;
                this.userModel = null;
                this.refresh();
            },
            btnsTemplate: function (e) {
                return {
                    template: Vue.component(BtnsTemplate.name, BtnsTemplate),
                    templateArgs: {
                        ...e,
                        parentComponent: this.$refs.grid,
                        btns: [
                            {
                                name: "edit",
                                text: "",
                                tooltip: this.$t('buttons.edit.btnText'),
                                class: 'btn btn-primary',
                                icon: "fas fa-edit",
                                show: this.hasUpdateRights,
                                clickHandler: this.editBtnClick
                            },
                            {
                                name: "management",
                                text: "",
                                tooltip: this.$t('buttons.management.btnText'),
                                class: 'btn btn-primary',
                                icon: "fas fa-user-cog",
                                show: this.hasUpdateRights,
                                clickHandler: this.manageBtnClick
                            },
                            {
                                name: "delete",
                                text: "",
                                tooltip: this.$t('buttons.delete.btnText'),
                                class: 'btn btn-danger',
                                icon: "fas fa-times",
                                show: this.hasDeleteRights,
                                clickHandler: this.deleteBtnClick
                            }
                        ]
                    }
                };
            },
            editBtnClick(args) {
                if (!this.hasUpdateRights) {
                    return this.$router.push(`/unauthorized`);
                }

                this.showUserEditModal(args.id);
            },
            showUserEditModal(id) {
                userService
                    .getById(id)
                    .then(response => {
                        let model = response.data;
                        if (model) {
                            this.userModel = model;
                            this.showUserEdit = true;
                        } else {
                            this.userModel = null;
                            this.showUserEdit = false;
                        }
                    })
                    .catch(error => {
                        this.userModel = null;
                        this.showUserEdit = false;

                        this.$vs.notify({
                            title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                            text: this.$helpers.errorText(error),
                            color: this.$helpers.errorColor(error),
                            time: this.$store.getters.notificationDuration
                        })
                    });
            },
            manageBtnClick(args) {
                if (!this.hasUpdateRights) {
                    return this.$router.push(`/unauthorized`);
                }

                let routeData = this.$router.resolve({ name: 'administration/user/manage', params: { id: args.id } });
                window.open(routeData.href, '_blank');
            },
            deleteBtnClick(args) {
                kendo.confirm(`${this.$t('administration.user.confirmations.delete')} '${args.userName}'?`)
                    .then(function () {
                        userService
                            .delete(args.id)
                            .then(response => {
                                this.refresh();
                                this.$vs.notify({
                                    title: this.$t('request.response.status.' + this.$helpers.responseStatusCode(response)),
                                    text: this.$helpers.responseMessage(response),
                                    color: 'success',
                                    time: this.$store.getters.notificationDuration
                                });
                            })
                            .catch(error => {
                                this.$vs.notify({
                                    title: this.$t('request.response.status.' + this.$helpers.errorStatusCode(error)),
                                    text: this.$helpers.errorText(error),
                                    color: this.$helpers.errorColor(error),
                                    time: this.$store.getters.notificationDuration
                                })
                            });
                    }.bind(this));
            },
            rolesDropDownEditor: function (container, options) {
                $('<input name="' + options.field + '"/> id="' + options.field + '"')
                    .appendTo(container)
                    .kendoMultiSelect({
                        autoBind: false,
                        dataTextField: 'name',
                        dataValueField: "id",
                        optionLabel: this.$t('selectTariffGroup'),
                        dataSource: {
                            transport: {
                                read: {
                                    url: this.$store.getters.baseUrl + '/api/roles/getAllModels',
                                    dataType: 'json',
                                    type: 'GET',
                                    contentType: 'application/json',
                                    beforeSend: this.beforeSend
                                },
                            }
                        }
                    });
            },
            rolesCellTemplate: function (dataItem) {
                return kendo.htmlEncode("");

                // if (dataItem.roleModels === null || !Array.isArray(dataItem.roleModels) || dataItem.roleModels.lenght <= 0) {
                // }

                // let templateHtml = "";
                // dataItem.roleModels.forEach(item => {
                //    templateHtml += '<span class="badge badge-secondary">' + kendo.htmlEncode(item.name) + '</span>';
                // });

                // return templateHtml;
            },
            deletedCellTemplate: function (dataItem) {
                let templateHtml =
                    '<div class="text-center"><span class="badge ' + (dataItem.deleted ? 'badge-danger' : 'badge-success') + '">' + kendo.htmlEncode(dataItem.deleted ? this.$t('yes') : this.$t('no')) + '</span></div>';

                return templateHtml;
            },
            refresh() {
                const ds = this.$refs.datasource1.kendoWidget();
                if (ds) {
                    ds.read();
                }
            },
            dataSourceError(e) {
                this.$vs.notify({
                    title: this.$t('notifications.error'),
                    text: this.$helpers.kendoDataSourceErrorText(e),
                    color: 'danger',
                    time: this.$store.getters.notificationDuration,
                });
            },
            datasorceRequestEnd(e) {
                if (e.type === "create" && e.response) {
                    this.refresh()
                } else if (e.type === "update" && e.response) {
                    this.refresh()
                } else if (e.type === "destroy" && e.response) {
                    this.refresh()
                }
            },
            clearFilters() {
                this.searchQuery = '';
                const grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    grid.dataSource.sort({});
                    grid.dataSource.page(1);
                    grid.dataSource.aggregate([]);
                    grid.dataSource.filter({});
                }
            },
            massSearch() {
                if (!this.searchQuery) {
                    this.$vs.notify({
                        title: this.$t('buttons.search.btnText'),
                        text: this.$t('emptySearchFilter'),
                        color: 'warning',
                        time: this.$store.getters.notificationDuration
                    })

                    return;
                }

                let grid = this.$refs.grid.kendoWidget();
                if (grid) {
                    let filter = this.$helpers.getMassFilterForGrid(grid, this.searchQuery);
                    if (filter) {
                        grid.dataSource.filter(filter);
                    }
                }
            },
            hasParentCellTemplate: function (dataItem) {
                let templateHtml =
                    '<div class="text-center"><span class="badge ' + (dataItem.hasParent ? 'badge-warning' : 'badge-secondary') + '">' + kendo.htmlEncode(dataItem.hasParent ? this.$t('yes') : this.$t('no')) + '</span></div>';

                return templateHtml;
            },
            createBtnClick() {
                if (!this.hasCreateRights) {
                    return this.$router.push(`/unauthorized`);
                }

                this.showUserCreate = true;
            },
        },
        mounted: function () {

        },
        created: function () {
            window.JSZip = JSZip // Необходимо е за да работи експорта в excel
        },
        computed: {
            ...mapGetters(['dateTimeFormat', 'isEnVersion']),
            customDatetimeFormat() {
                return `{0:${this.dateTimeFormat}}`;
            },
            token: function () {
                return this.$store.getters.authToken;
            },
            toolbar: function () {
                return [{ name: 'excel', text: this.$t('buttons.excelExport.tooltipText') }]
            },
            hasReadRights() {
                return this.$store.getters.claims.aspNetUser.read;
            },
            hasUpdateRights() {
                return this.$store.getters.claims.aspNetUser.update;
            },
            hasDeleteRights() {
                return this.$store.getters.claims.aspNetUser.delete;
            },
            hasCreateRights() {
                return this.$store.getters.claims.aspNetUser.create;
            },
            hasPersonReadRights() {
                return this.$store.getters.claims.person.read
                    || this.$store.getters.claims.aspNetUser.read;
            },
            hasPersonUpdateRights() {
                return this.$store.getters.claims.person.update
                    || this.$store.getters.claims.aspNetUser.update
                    || this.$store.getters.claims.aspNetUser.create
                    || this.$store.getters.claims.person.create;
            },
        }
    }
</script>

<style scoped>
    .btn {
        margin-right: 10px;
        margin-left: 5px;
    }

    .checkbox {
        margin-right: 5px;
    }

    .selectFile {
        display: none;
    }

    .inlineForm {
        display: inline;
    }

    /*
        Use the DejaVu Sans font for display and embedding in the PDF file.
        The standard PDF fonts have no support for Unicode characters.
    */
    .k-pdf-export .k-grid {
        font-family: "DejaVu Sans", "Arial", sans-serif;
    }

    .k-pdf-export .k-filterable .k-grid-filter {
        display: none;
    }

    .k-pdf-export .k-with-icon .k-header-column-menu {
        display: none;
    }

    .k-grid .k-grid-header .k-header .k-link {
        height: auto;
    }

    .k-grid .k-grid-header .k-header {
        white-space: normal;
    }

    .k-grid-edit, .k-grid-details, .k-grid-delete, .k-grid-pdf, .k-grid-excel {
        padding: 0;
        margin-bottom: 2px;
    }
</style>

<style lang="scss">
    tr.left-border-danger {
        &:hover

    {
        color: rgba(255, 0, 0, 0.7) !important;
    }

    td:first-child {
        border-left: 5px solid rgba(255, 0, 0, 0.5) !important;
    }

    }

    tr.left-border-blue {
        &:hover

    {
        color: #3498db !important;
    }

    td:first-child {
        border-left: 5px solid #3498db !important;
    }

    }

    tr.left-border-green {
        &:hover

    {
        color: #4caf50 !important;
    }

    td:first-child {
        border-left: 5px solid #4caf50 !important;
    }
    }
</style>
